// Tiny module-level stores for the compose feature — the few things that cross component
// boundaries without a prop chain through the shared conversation files:
//
// - the FORWARD request (a row's menu → the one ForwardHost the workspace mounts),
// - the EDIT request (↑ in an empty composer → the row whose post it names opens its inline
//   editor, and focus comes back to that composer when the editor closes),
// - the `~channel` CATALOGUE of the open team (slug → channel id), read once per team from the
//   store and refreshed when channels or memberships change; MessageBody draws `~slug` links
//   from it and the composer's `~` strip offers it.
import { useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import { data } from '../../../bridge/client';
import type { ChannelLinkTarget } from '../../../core/channelLinks';
import type { PostRecord } from '../../../core/records';
import { ShellContext } from '../../shell/ShellContext';

type Listener = () => void;

function store<T>(initial: T) {
  let value = initial;
  const listeners = new Set<Listener>();
  return {
    get: () => value,
    set(next: T) {
      value = next;
      for (const l of listeners) l();
    },
    subscribe(l: Listener) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

// MARK: - Forward

export interface ForwardRequest {
  post: PostRecord;
  /** Who wrote it — the preview's eyebrow. */
  authorName: string;
}

const forwardStore = store<ForwardRequest | null>(null);

export function openForward(request: ForwardRequest): void {
  forwardStore.set(request);
}

export function closeForward(): void {
  forwardStore.set(null);
}

export function useForwardRequest(): ForwardRequest | null {
  return useSyncExternalStore(forwardStore.subscribe, forwardStore.get);
}

// MARK: - Edit last message

interface EditRequest {
  postId: string;
  inThread: boolean;
  token: number;
  returnFocus: HTMLElement | null;
}

const editStore = store<EditRequest | null>(null);
let editTokens = 0;

export function requestEdit(postId: string, inThread: boolean, returnFocus: HTMLElement | null): void {
  editStore.set({ postId, inThread, token: ++editTokens, returnFocus });
}

/**
 * Called by every message row: opens the row's inline editor when an edit request names it, and
 * hands focus back to the composer that asked once that editor closes. Each row only re-renders
 * when the answer for IT changes (the snapshot is a number).
 */
export function useEditRequest(postId: string, inThread: boolean, editing: boolean, setEditing: (v: boolean) => void): void {
  const token = useSyncExternalStore(editStore.subscribe, () => {
    const r = editStore.get();
    return r && r.postId === postId && r.inThread === inThread ? r.token : 0;
  });
  const handled = useRef(0);
  const viaKeyboard = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!token || token === handled.current) return;
    handled.current = token;
    viaKeyboard.current = editStore.get()?.returnFocus ?? null;
    setEditing(true);
  }, [token, setEditing]);
  useEffect(() => {
    if (editing || !viaKeyboard.current) return;
    const el = viaKeyboard.current;
    viaKeyboard.current = null;
    requestAnimationFrame(() => el.isConnected && el.focus());
  }, [editing]);
}

// MARK: - Channel catalogue (`~`)

interface Catalogue {
  teamId: string;
  joined: ChannelLinkTarget[];
  slugs: ReadonlySet<string>;
  idBySlug: ReadonlyMap<string, string>;
}

const EMPTY: Catalogue = { teamId: '', joined: [], slugs: new Set(), idBySlug: new Map() };
const catalogueStore = store<Catalogue>(EMPTY);
let loadingTeam = '';
let unwatch: (() => void) | null = null;

function load(teamId: string): void {
  loadingTeam = teamId;
  void data.call('channelCatalogue', teamId).then(
    (r) => {
      if (loadingTeam !== teamId) return;
      const joined = r.joined;
      catalogueStore.set({
        teamId,
        joined,
        slugs: new Set(joined.map((c) => c.slug)),
        idBySlug: new Map(joined.map((c) => [c.slug, c.channelId])),
      });
    },
    () => undefined,
  );
}

function ensure(teamId: string): void {
  if (!teamId || loadingTeam === teamId) return;
  load(teamId);
  unwatch?.();
  let scheduled = false;
  unwatch = data.onChanges((tables) => {
    if (scheduled || !(tables.has('channels') || tables.has('channelMembers'))) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      if (loadingTeam) load(loadingTeam);
    }, 300);
  });
}

/** The open team's `~` catalogue (empty until the first read lands). */
export function useChannelCatalogue(): Catalogue {
  const teamId = useContext(ShellContext)?.team.id ?? '';
  useEffect(() => ensure(teamId), [teamId]);
  const value = useSyncExternalStore(catalogueStore.subscribe, catalogueStore.get);
  return value.teamId === teamId ? value : EMPTY;
}
